// AES-256-GCM payload encryption matching the backend's payload-encryption.interceptor.ts.
// Wire format: { payload: base64(iv[12] | authTag[16] | ciphertext) }
// Note: the key ships in the bundle, so this is obfuscation on top of HTTPS, not a security boundary.

const KEY_HEX: string | undefined = import.meta.env.VITE_API_AES_KEY;
const IV_LENGTH = 12;
const TAG_LENGTH = 16;

export const isPayloadEncryptionEnabled = !!KEY_HEX && /^[0-9a-fA-F]{64}$/.test(KEY_HEX);

const hexToBytes = (hex: string) =>
  Uint8Array.from(hex.match(/.{2}/g)!.map((b) => parseInt(b, 16)));

let keyPromise: Promise<CryptoKey> | null = null;
const getKey = () => {
  if (!keyPromise) {
    keyPromise = crypto.subtle.importKey('raw', hexToBytes(KEY_HEX!), 'AES-GCM', false, [
      'encrypt',
      'decrypt',
    ]);
  }
  return keyPromise;
};

// Chunked to avoid "Maximum call stack size exceeded" on large payloads.
const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

const base64ToBytes = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export async function encryptPayload(body: unknown): Promise<{ payload: string }> {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const plain = new TextEncoder().encode(JSON.stringify(body));
  // Web Crypto returns ciphertext | tag; the backend expects iv | tag | ciphertext.
  const out = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: TAG_LENGTH * 8 }, key, plain)
  );
  const ciphertext = out.subarray(0, out.length - TAG_LENGTH);
  const tag = out.subarray(out.length - TAG_LENGTH);

  const buf = new Uint8Array(IV_LENGTH + TAG_LENGTH + ciphertext.length);
  buf.set(iv, 0);
  buf.set(tag, IV_LENGTH);
  buf.set(ciphertext, IV_LENGTH + TAG_LENGTH);
  return { payload: bytesToBase64(buf) };
}

export async function decryptPayload<T = unknown>(payload: string): Promise<T> {
  const key = await getKey();
  const buf = base64ToBytes(payload);
  const iv = buf.subarray(0, IV_LENGTH);
  const tag = buf.subarray(IV_LENGTH, IV_LENGTH + TAG_LENGTH);
  const ciphertext = buf.subarray(IV_LENGTH + TAG_LENGTH);

  // Web Crypto wants ciphertext | tag.
  const joined = new Uint8Array(ciphertext.length + TAG_LENGTH);
  joined.set(ciphertext, 0);
  joined.set(tag, ciphertext.length);

  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: TAG_LENGTH * 8 },
    key,
    joined
  );
  return JSON.parse(new TextDecoder().decode(plain)) as T;
}

/** Only plain JSON objects/arrays are encrypted; FormData, Blob, strings, etc. pass through. */
export const shouldEncryptBody = (body: unknown) => {
  if (body === null || body === undefined || typeof body !== 'object') return false;
  if (Array.isArray(body)) return true;
  const proto = Object.getPrototypeOf(body);
  return proto === Object.prototype || proto === null;
};

/** Successful responses arrive as { payload: "<base64>" }; error responses stay plaintext. */
export const isEncryptedResponse = (data: unknown): data is { payload: string } =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as { payload?: unknown }).payload === 'string' &&
  Object.keys(data).length === 1;
